/**
 * CORE-1141 — как продуктовый отчёт мастера превращается в то, что он читает.
 *
 * Модуль без зависимостей от React Native: здесь решения, и их нужно проверять
 * тестами. Главное из них одно и повторяется всюду — **`null` не ноль**.
 *
 * У этого отчёта необязательное число почти всегда означает «не наблюдалось»:
 * отписки не записывались до CORE-1141, показы эфиров не несли мастера, витрина
 * программы не отправляла событий вовсе. Показать вместо этого ноль значит
 * сказать мастеру «никто не отписался» и «никто не смотрел» — то есть соврать
 * ровно там, где он собирается принимать решение.
 */

import type {
  MasterProfileStats,
  MasterLivesStats,
  MasterProductsStats,
} from '../../types/masterAnalytics';

/** Целое число зрителей/просмотров. Отсутствие — прочерк, а не ноль. */
export function count(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(value);
}

/** Доля в процентах. Null — «нет данных», а не «ноль процентов». */
export function share(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 1000) / 10}%`;
}

/** Минуты присутствия. */
export function minutes(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value < 1) return 'меньше минуты';
  return `${Math.round(value)} мин`;
}

/**
 * Динамика подписок словами.
 *
 * Пока отписки не наблюдаются, прироста НЕ СУЩЕСТВУЕТ, и подставлять вместо
 * него число подписавшихся нельзя: это разные величины, а выглядят одинаково
 * хорошо.
 */
export function followerGrowth(profile: MasterProfileStats): string {
  if (profile.followersNet == null) {
    return `+${profile.followersGained}, отписки пока не считаются`;
  }
  const sign = profile.followersNet > 0 ? '+' : '';
  return `${sign}${profile.followersNet} (пришли ${profile.followersGained}, ушли ${profile.followersLost})`;
}

/**
 * Почему у показов Live-сессий прочерк. Пустая строка — показы наблюдаемы,
 * объяснять нечего.
 */
export function livesImpressionsHint(lives: MasterLivesStats): string {
  if (lives.impressions != null) return '';
  if (lives.impressionsObservableFromUtc == null) {
    return 'Показы карточек Live-сессий ещё не собираются. Входы и присутствие при этом настоящие — их считает сервер.';
  }
  return `Показы собираются с ${lives.impressionsObservableFromUtc.slice(0, 10)}, а период начинается раньше.`;
}

/** Почему у конверсии программ прочерк. */
export function productsHint(products: MasterProductsStats): string {
  if (products.conversionAvailable) return '';
  return products.conversionUnavailable
    ?? 'Показы витрины ещё не собираются, поэтому конверсия не считается.';
}

/**
 * Доля досмотров с честным знаменателем рядом.
 *
 * Голая доля без числа стартов одинаково выглядит и у практики с тысячей
 * просмотров, и у практики с одним.
 */
export function completionLabel(completions: number, starts: number, rate: number | null): string {
  if (starts === 0) return 'просмотров не было';
  return `${share(rate)} · ${completions} из ${starts}`;
}

/** Есть ли вообще что показывать в разделе. */
export function isEmptyReport(report: {
  profile: MasterProfileStats;
  practices: { starts: number; opens: number };
  lives: { joins: number };
  products: { purchases: number };
}): boolean {
  return report.profile.views === 0
    && report.profile.followersTotal === 0
    && report.practices.opens === 0
    && report.practices.starts === 0
    && report.lives.joins === 0
    && report.products.purchases === 0;
}
